import { Box, Group, Menu, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { EntityType } from '@repo/common/enums/entityType';
import { Button, Icon, Text } from '@repo/foundations';
import { useNavigate } from 'react-router';
import type { ISecodaEntity, ITemplate } from '../../api';
import { useTemplateList } from '../../api/hooks/templates';

import { useIsTeamsPage } from '../../api/hooks/team';
import type { SecodaEntity } from '../../lib/models/entity';
import { openConfirmModal } from '../ModalManager';
import { TemplateModal } from './TemplateModal';

import { UserRole } from '@repo/common/enums/UserRole';
import SplitButton from '@repo/foundations/components/Buttons/SplitButton';
import type { ComponentProps, Ref } from 'react';
import { useCheckIAMPermission } from '../../utils/authorization/roles';

type TemplateButtonSplitButtonProps = Omit<
	ComponentProps<typeof SplitButton>,
	'children'
> & {
	ref?: Ref<HTMLButtonElement>;
};

interface TemplatesButtonProps {
	type: EntityType;
	children: JSX.Element;
	splitButtonProps?: TemplateButtonSplitButtonProps;
	handleUseTemplate: (template: ITemplate) => void;
	entity?: SecodaEntity | ISecodaEntity;
	useConfirmation?: boolean;
}

export function TemplatesButton({
	type,
	children,
	splitButtonProps,
	handleUseTemplate,
	entity,
	useConfirmation,
}: TemplatesButtonProps) {
	const theme = useMantineTheme();
	const navigate = useNavigate();
	const [isOpen, { toggle: onToggle }] = useDisclosure();
	const [isOpenTemplateModal, { toggle: onToggleTemplateModal }] =
		useDisclosure();
	const { teamId } = useIsTeamsPage();

	// This is based on the profileTabs in SettingsPage.tsx
	const { hasPermission: canManageTemplates } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Workspace.Write',
	});

	const { data: templates } = useTemplateList({
		filters: {
			entity_type_template: type,
			usable_by_teams: teamId ?? undefined,
		},
		options: {
			select: (data) => data?.results || [],
		},
	});

	const excludedTypes = [
		EntityType.question,
		EntityType.glossary,
		EntityType.document,
		EntityType.collection,
	];

	const catalogTemplate = !excludedTypes.includes(type);

	const handleUseTemplateWithConfirmation = (template: ITemplate) => {
		if (entity && useConfirmation) {
			openConfirmModal({
				title: 'Apply template',
				children: (
					<Text size="sm">
						Using this template will overwrite your current data. This action
						cannot be undone. Are you sure you want to continue?
					</Text>
				),
				labels: { confirm: 'Apply template', cancel: 'Cancel' },
				confirmProps: {
					variant: 'primary',
					tone: 'default',
				},
				onConfirm: () => handleUseTemplate(template),
			});
		} else {
			handleUseTemplate(template);
		}
	};

	const menu = (
		<>
			<Menu
				opened={isOpen}
				onChange={onToggle}
				position="bottom-end"
				withinPortal
			>
				<Menu.Target>
					<Box
						sx={{
							'& svg': {
								stroke: theme.other.getColor('icon/nav/default'),
							},
						}}
						data-testid="template-chevron-down"
					>
						{children}
					</Box>
				</Menu.Target>
				<Menu.Dropdown>
					{templates?.map((t) => (
						<Menu.Item
							onClick={() => handleUseTemplateWithConfirmation(t)}
							key={t.id}
							data-testid={`template-${t.id}`}
							rightSection={
								<Group display="flex" spacing={0} ml={theme.spacing.sm}>
									{t.is_default && (
										<Icon name="flag" color="icon/secondary/default" />
									)}
								</Group>
							}
						>
							{t.name}
						</Menu.Item>
					))}
					{templates &&
						templates.length > 0 &&
						((catalogTemplate && entity) || canManageTemplates) && (
							<Menu.Divider />
						)}
					{catalogTemplate && entity && (
						<Menu.Item
							data-testid="new-template-button"
							icon={<Icon name="fileExport" />}
							onClick={() => {
								onToggleTemplateModal();
							}}
						>
							Convert doc to template
						</Menu.Item>
					)}
					{canManageTemplates && (
						<Menu.Item
							data-testid="new-template-button"
							icon={<Icon name="settings" />}
							onClick={() => {
								navigate(`/settings/templates`);
							}}
						>
							Manage templates
						</Menu.Item>
					)}
				</Menu.Dropdown>
			</Menu>

			{entity && (
				<TemplateModal
					opened={isOpenTemplateModal}
					onClose={onToggleTemplateModal}
					type={type}
					entity={entity}
				/>
			)}
		</>
	);
	const shouldRender =
		(templates && templates.length > 0) ||
		canManageTemplates ||
		(catalogTemplate && entity);

	// If the dropdown is part of a Split button and shouldRender is false, then we can render a regular button
	if (splitButtonProps) {
		if (!shouldRender)
			return (
				<Button
					onClick={splitButtonProps.onClick}
					variant={splitButtonProps.variant}
					ref={splitButtonProps.ref}
				>
					{splitButtonProps.label}
				</Button>
			);
		return <SplitButton {...splitButtonProps}>{menu}</SplitButton>;
	}

	if (!shouldRender) return null;
	return menu;
}
